/**
 * SMS encoding + segment counter — mirrors the backend's `CalculateSegments`
 * in `api/internal/services/segments.go` so the UI counter matches what the
 * provider will be billed for.
 *
 * Rules:
 *  - If every character is in the GSM-7 base + extension table, the message is
 *    GSM-7. Otherwise it is UCS-2 (UTF-16, common when emojis or non-Latin
 *    glyphs appear).
 *  - Single-segment limits: 160 chars (GSM-7) or 70 chars (UCS-2).
 *  - Multi-segment limits: 153 chars/segment (GSM-7) or 67 chars/segment (UCS-2)
 *    — providers reserve 7 chars for the User Data Header that stitches segments
 *    back together on the receiver.
 *  - GSM-7 extension chars (`{}[]~^|\€` and a few others) count as 2 characters
 *    each because they are encoded as an escape + the base char.
 */

export type SmsEncoding = 'GSM7' | 'UCS2';

const GSM7_BASE = new Set<string>(
  [
    // Lowercase
    ...'abcdefghijklmnopqrstuvwxyz',
    // Uppercase
    ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    // Digits
    ...'0123456789',
    // Spanish / Latin extras
    ...'@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ',
    // Punctuation + space
    ...' !"#¤%&\'()*+,-./:;<=>?¡ÄÖÑÜ§¿äöñüà\n\r',
  ].flatMap((c) => [c]),
);

const GSM7_EXTENSION = new Set<string>(['{', '}', '[', ']', '~', '^', '|', '\\', '€', '\f']);

export interface SegmentInfo {
  encoding: SmsEncoding;
  /** Number of SMS parts the carrier will bill for. */
  segments: number;
  /** Effective character count after counting GSM-7 extension chars as 2. */
  characters: number;
  /** Per-segment capacity in characters (160/70 or 153/67). */
  perSegment: number;
  /** Characters left in the current segment before a new one is consumed. */
  remaining: number;
}

function detectEncoding(body: string): SmsEncoding {
  for (const ch of body) {
    if (GSM7_BASE.has(ch)) continue;
    if (GSM7_EXTENSION.has(ch)) continue;
    return 'UCS2';
  }
  return 'GSM7';
}

function countCharacters(body: string, encoding: SmsEncoding): number {
  if (encoding === 'UCS2') {
    // UCS-2 counts code units. Most BMP chars are 1 unit; supplementary planes
    // (e.g. many emoji) are surrogate pairs = 2 units. Iterating with [...body]
    // gives code points, so we count UTF-16 units explicitly via .length on the
    // string, which is what carriers bill.
    return body.length;
  }
  let count = 0;
  for (const ch of body) {
    count += GSM7_EXTENSION.has(ch) ? 2 : 1;
  }
  return count;
}

export function calculateSegments(body: string): SegmentInfo {
  if (body.length === 0) {
    return { encoding: 'GSM7', segments: 0, characters: 0, perSegment: 160, remaining: 160 };
  }
  const encoding = detectEncoding(body);
  const characters = countCharacters(body, encoding);
  const singleLimit = encoding === 'GSM7' ? 160 : 70;
  const multiLimit = encoding === 'GSM7' ? 153 : 67;

  let segments: number;
  let perSegment: number;
  if (characters <= singleLimit) {
    segments = 1;
    perSegment = singleLimit;
  } else {
    segments = Math.ceil(characters / multiLimit);
    perSegment = multiLimit;
  }

  const used = segments === 1 ? characters : characters - (segments - 1) * multiLimit;
  const remaining = Math.max(0, perSegment - used);

  return { encoding, segments, characters, perSegment, remaining };
}

const E164_REGEX = /^\+[1-9]\d{6,14}$/;

/** True if the string is a valid E.164 phone number (e.g. `+15551234567`). */
export function isValidE164(value: string): boolean {
  return E164_REGEX.test(value.trim());
}
